/**
 * vr-render.js — THE single source of truth for /video-to-vr output geometry.
 *
 * Before this file the tool had three descriptions of "what the output frame
 * looks like" and all three disagreed:
 *
 *   1. the FFmpeg filter chain built in the blade  (the file the user gets)
 *   2. the WebGL shader in vr-preview.js           (what the user judged by)
 *   3. nothing at all for a hardware lane          (there wasn't one)
 *
 * Measured 2026-08-19, the disagreement between (1) and (2) was not cosmetic:
 * FFmpeg's `lenscorrection` normalises the radius by the frame's HALF-DIAGONAL
 * and is isotropic in PIXEL space, while the preview shader normalised by half
 * width and half height separately in UV space. On an 800x400 frame with
 * k1=-0.3 a source line at x=600 lands at x=613..615 through the real filter
 * (measured on ffmpeg itself); the preview's maths puts it at ~604 and bends
 * the frame elliptically. Same knob, two pictures.
 *
 * So geometry lives here once, and is emitted two ways that a test proves
 * equal:
 *
 *   VRRender.geometry(s, srcW, srcH)    -> output size + per-eye size + cap info
 *   VRRender.ffmpegChain(s, srcW, srcH) -> the -vf string for the FFmpeg lane
 *   VRRender.createRenderer({...})      -> WebGL rig for the hardware lane
 *   VRRender.GLSL_DISTORT               -> the lens term, shared with vr-preview
 *
 * -- Why there is an output cap ------------------------------------------
 * Side-by-side DOUBLES the frame width. A 4K phone clip (the most common input
 * this tool gets) therefore asks for a 7680x2160 H.264 encode, which is
 * Level 6.0 — measured with ffprobe on our own output. No phone decodes
 * Level 6.0; Apple's hardware decoder tops out at 5.2. That is the device the
 * tool exists for: a phone in a cardboard viewer. So the tool was spending
 * ~14 minutes of WASM x264 on a fast desktop (measured: 80 s per 2 s of 4K
 * source) to produce a file the target device cannot open — and on a phone the
 * tab was killed long before it got there.
 *
 * The ceiling is the H.264 Level 5.1 coded area, 3840*2160 = 8 294 400 px —
 * the same constant webcodecs-pipeline.js already enforces for its own encoder
 * (it learned it from 8 encoder rejections in /pino telemetry 2026-05-08).
 * Sharing the number is the point: if the two lanes capped differently, the
 * user's file would change shape depending on which browser they opened it in.
 */
(function(){
'use strict';
if (window.VRRender) return;

/* H.264 Level 5.1 coded area. Same constant as webcodecs-pipeline.js —
   both lanes must land on the same output or the tool has two answers. */
var MAX_CODED_AREA = 8294400;

/* Equirectangular canvases for sphere mode (2:1 by definition). */
var SPHERE_CANVAS = { lite:[1920,960], '2k':[2880,1440], '4k':[3840,1920] };

/* Per-EYE ceilings. The old picker fitted the whole ultra-wide SBS frame into
   a 16:9 box: "1920x1080 SBS" scaled a 32:9 frame down to 1920x540 and padded
   it to 1920x1080, so half the delivered frame was black bars and each eye got
   960x540. Measured on our own output 2026-08-19 (top and bottom 200 rows,
   mean luma 0.0). Per-eye targets keep the source aspect and have no bars. */
var PER_EYE = {
    'source': null,
    '1440p':  [2560, 1440],
    '1080p':  [1920, 1080],
    '720p':   [1280, 720]
};

function even(n){ n = Math.round(n); return n % 2 ? n - 1 : n; }

/* Are these dimensions a real picture, or a browser shrugging? Chromium reports
   videoWidth/videoHeight 0 for an HEVC mp4 it cannot decode while still firing
   loadedmetadata with a correct duration and no error — and an iPhone shooting
   in "High Efficiency" produces exactly that file. Anything under 16 px is not
   a video, it is a missing answer, and the chain below must not turn it into a
   `scale=4:2` that ships as a successful conversion. */
function dimsKnown(w, h){ return (w | 0) >= 16 && (h | 0) >= 16; }

/* Lens pre-distortion, GLSL. Same maths as FFmpeg's lenscorrection: radius
   normalised by the half-diagonal, isotropic in PIXEL space (hence the aspect
   term), mapping output -> input. Shared with vr-preview.js so the preview
   cannot drift from the file again. */
var GLSL_DISTORT = [
'vec2 vrLensDistort(vec2 uv, vec2 k, float aspect){',
'    if (k.x == 0.0 && k.y == 0.0) return uv;',
'    vec2 c = uv - 0.5;',
'    vec2 d = vec2(c.x * aspect, c.y);',
'    float halfDiag = 0.5 * sqrt(aspect * aspect + 1.0);',
'    vec2 dn = d / halfDiag;',
'    float r2 = dot(dn, dn);',
'    float f = 1.0 + k.x * r2 + k.y * r2 * r2;',
'    vec2 o = dn * f * halfDiag;',
'    return vec2(o.x / aspect, o.y) + 0.5;',
'}'
].join('\n');

/* ── The lens, and the pre-distortion that cancels it ────────────────────
 *
 * HEADSET_LENS is the PHYSICS: the pincushion each viewer's plastic applies,
 * in the same r*(1 + k1*r^2 + k2*r^4) form FFmpeg's lenscorrection uses.
 * Published values for Cardboard v2 and Gear VR; generic plastic interpolated.
 *
 * HEADSET_BARREL is what we BAKE, and it is the inverse of that — which is NOT
 * the same polynomial with negated coefficients. That was the standing
 * assumption and it is wrong by a lot: negating gives only a first-order
 * inverse, and the error grows as k^2*r^4, i.e. worst exactly at the edges the
 * tooltip tells people to judge by ("если края всё ещё кривые — измените").
 *
 * MEASURED 2026-08-19 by pushing a grid through the REAL filter twice —
 * `lenscorrection=<baked>` then `lenscorrection=<lens>` — which should return
 * the grid unchanged. Mean |diff| over the frame (5% border), against a
 * control of two identity passes scoring 0.00:
 *
 *              full frame   centre   outer ring
 *   negated       19.15       4.06      25.99      <- what shipped
 *   inverse        5.79       3.07       7.12      <- this table
 *
 * The coefficients below minimise mean radial displacement over the frame.
 * ⚠️ Two things worth knowing before touching them:
 *   - an ANALYTIC minimax fit (-0.247, 0.090) looked better on paper and was
 *     WORSE through the real filter in every band, because its advantage lived
 *     in corner samples the filter clamps off-frame anyway. Fit against
 *     ffmpeg, not against the model of ffmpeg;
 *   - the physics half (HEADSET_LENS) is unverified against a real viewer —
 *     nobody here has a Cardboard to hold. This change fixes OUR arithmetic,
 *     which is wrong under any reading of those numbers; if the lens table
 *     itself is off, the fix is to correct the table, and the inverse follows.
 * `tests/js/vr-geometry-and-op-inference.test.mjs` re-derives the pairs from
 * HEADSET_LENS and fails if the two ever drift apart.
 */
var HEADSET_LENS = {
    cardboard: { k1: 0.30, k2:-0.05 },   /* Cardboard v2 reference */
    gearvr:    { k1: 0.22, k2:-0.03 },   /* flatter lens, less to undo */
    generic:   { k1: 0.26, k2:-0.04 },   /* mid-range plastic viewers */
    custom:    { k1: 0.30, k2:-0.05 }    /* baseline, nudged by IPD below */
};
var HEADSET_BARREL = {
    cardboard: { k1:-0.259, k2:0.109 },  /* residual 2.1 px on 4K (negated: 52.4) */
    gearvr:    { k1:-0.199, k2:0.074 },  /* residual 1.1 px on 4K (negated: 30.9) */
    generic:   { k1:-0.230, k2:0.092 },  /* residual 1.6 px on 4K (negated: 41.0) */
    custom:    { k1:-0.259, k2:0.109 }   /* baseline, nudged by IPD below */
};
var BARREL_MULT = { off:0, light:0.5, medium:1.0, strong:1.7 };

/* Effective (k1,k2) for the current settings, or null when barrel is off. */
function barrelK(s){
    var base = HEADSET_BARREL[s.headset] || HEADSET_BARREL.cardboard;
    var m = BARREL_MULT[s.barrel];
    if (m == null || m === 0 || s.mode === 'sphere') return null;
    var k1 = base.k1, k2 = base.k2;
    if (s.headset === 'custom') {
        var drift = ((s.ipd || 63) - 63) / 20;   /* -0.4..+0.45 across 55..72 mm */
        k1 *= (1 + drift * 0.15);
        k2 *= (1 + drift * 0.10);
    }
    return { k1: k1 * m, k2: k2 * m };
}

/* Parallax shift as a FRACTION of source width. slider 10 -> 1%, 60 -> 6%. */
function parallaxFrac(s){
    var f = (s.parallax || 40) / 1000;
    if (s.headset === 'custom') f *= ((s.ipd || 63) / 63);
    return f;
}

/**
 * Output geometry for the current settings and source size.
 * Returns { outW, outH, eyeW, eyeH, capped, requestedW, requestedH, sphere }.
 * `capped` is true when the >4K ceiling engaged — the UI must SAY so
 * (feedback_no_silent_quality_downgrade: a quality change the user did not ask
 * for has to be visible up front, not a toast after the fact).
 */
function geometry(s, srcW, srcH){
    var known = dimsKnown(srcW, srcH);
    srcW = Math.max(2, srcW|0); srcH = Math.max(2, srcH|0);
    if (s.mode === 'sphere') {
        var cv = SPHERE_CANVAS[s.sphereRes] || SPHERE_CANVAS['2k'];
        /* Sphere output is a fixed equirectangular canvas, so it is right even
           when the source size is unknown — the fit happens inside it. */
        return { outW: cv[0], outH: cv[1], eyeW: cv[0], eyeH: cv[1], known: true,
                 capped: false, requestedW: cv[0], requestedH: cv[1], sphere: true };
    }
    /* Passthrough takes a source that is ALREADY side-by-side, so its per-eye
       size is half the source width — the frame is not doubled a second time. */
    var baseEyeW = (s.mode === 'passthrough') ? srcW / 2 : srcW;
    var baseEyeH = srcH;

    var lim = PER_EYE[s.resolution] || null;
    var eyeW = baseEyeW, eyeH = baseEyeH;
    if (lim) {
        var k = Math.min(lim[0] / baseEyeW, lim[1] / baseEyeH, 1);
        eyeW = baseEyeW * k; eyeH = baseEyeH * k;
    }
    var outW = even(eyeW * 2), outH = even(eyeH);
    var reqW = outW, reqH = outH, capped = false;
    if (outW * outH > MAX_CODED_AREA) {
        var c = Math.sqrt(MAX_CODED_AREA / (outW * outH));
        outW = even(outW * c); outH = even(outH * c);
        capped = true;
    }
    outW = Math.max(2, outW); outH = Math.max(2, outH);
    return { outW: outW, outH: outH, eyeW: Math.max(1, outW / 2), eyeH: outH,
             known: known, capped: capped && known,
             requestedW: reqW, requestedH: reqH, sphere: false };
}

/**
 * The FFmpeg -vf chain producing exactly the frame `geometry()` describes.
 * Order mirrors the shader: lens pre-distortion applies to the SOURCE, then the
 * frame is split/stacked, then scaled to the output size.
 * `opts.noBarrel` drops the lens term (some WASM builds lack the filter).
 */
function ffmpegChain(s, srcW, srcH, opts){
    opts = opts || {};
    var g = geometry(s, srcW, srcH);
    if (s.mode === 'sphere') {
        var vidW = Math.max(2, Math.round(g.outW * ((s.coverage || 180) / 360)));
        return 'scale=' + vidW + ':' + g.outH + ':force_original_aspect_ratio=decrease,' +
               'pad=' + g.outW + ':' + g.outH + ':(ow-iw)/2:(oh-ih)/2:black';
    }
    var f = [];
    var bk = opts.noBarrel ? null : barrelK(s);
    if (bk) f.push('lenscorrection=k1=' + bk.k1.toFixed(4) + ':k2=' + bk.k2.toFixed(4));
    if (s.mode === 'sbs') {
        f.push('split=2[l][r];[l][r]hstack=inputs=2');
    } else if (s.mode === 'parallax') {
        /* The offset is a FRACTION of the frame, not a pixel count — which is
           what the shader uses (uParallaxFrac), so the two lanes now say the
           same thing in the same units. It also cannot be poisoned by an
           unknown source size: as pixels, `round(srcW * frac)` on an unmeasured
           source collapsed to the 2 px floor, i.e. the depth effect silently
           disappeared instead of failing. Both crops share one expression, so
           the halves stay identical widths for hstack. */
        var pf = parallaxFrac(s).toFixed(6);
        var cw = 'trunc(iw*(1-' + pf + ')/2)*2';
        f.push('split=2[l][r];[l]crop=' + cw + ':ih:0:0[lc];[r]crop=' + cw + ':ih:iw-' + cw + ':0[rc];[lc][rc]hstack');
    }
    /* passthrough: the source already carries both eyes — no stacking. */
    f.push(scaleStep(s, g, srcW, srcH));
    return f.join(',');
}

/* The scale step, in two forms that implement the SAME rule.
 *
 * When the source size is known we pin exact numbers — that is what the parity
 * test compares against the shader, and exact beats clever.
 *
 * When it is NOT known we must not invent one. Pinning a guess is how an HEVC
 * mov whose <video> reported 0x0 came back as a 4x2 file that the tool called a
 * success. So the ceiling is handed to FFmpeg as arithmetic over the REAL
 * frame: `iw`/`ih` here are the frame AFTER the split/stack, so the per-eye
 * width is iw/2. Measured against the JS geometry on the same sources — 4K SBS
 * capped gives 5430x1526 both ways, 720p-per-eye gives 2560x720 from both a 4K
 * and a 1080p source. Commas inside the expression are escaped: the filtergraph
 * parser splits on bare commas.
 */
function scaleStep(s, g, srcW, srcH){
    if (g.known) return 'scale=' + g.outW + ':' + g.outH + ':flags=lanczos';
    var terms = ['1'];
    var lim = PER_EYE[s.resolution] || null;
    if (lim) { terms.push('2*' + lim[0] + '/iw'); terms.push(lim[1] + '/ih'); }
    terms.push('sqrt(' + MAX_CODED_AREA + '/(iw*ih))');
    var k = terms.reduceRight(function(acc, t){ return acc ? 'min(' + t + '\\,' + acc + ')' : t; }, '');
    return 'scale=w=trunc(iw*' + k + '/2)*2:h=trunc(ih*' + k + '/2)*2:flags=lanczos';
}

/* -- WebGL rig for the hardware lane ------------------------------------- */

var VERT = [
'attribute vec2 aPos;','varying vec2 vUv;',
'void main(){ vUv = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }'
].join('\n');

var FRAG = [
'precision highp float;',
'uniform sampler2D uTex;',
'uniform int   uMode;',          /* 0 sbs | 1 parallax | 2 passthrough | 3 sphere */
'uniform vec2  uBarrelK;',
'uniform float uSrcAspect;',     /* source w/h — for the isotropic lens term */
'uniform float uParallaxFrac;',
'uniform float uSphereHalfW;',
'uniform float uSphereHalfH;',
'uniform int   uRotation;',      /* 0 | 90 | 180 | 270 — tkhd rotation, see below */
'varying vec2 vUv;',
GLSL_DISTORT,
/* A decoded VideoFrame carries the CODED picture; the mp4 tkhd matrix (which
   every phone writes) is container metadata the decoder does not apply. The
   <video> element does apply it, so without this the preview and the hardware
   lane would disagree on which way is up for any clip shot in portrait.
   `uRotation` is the angle the player turns the coded frame CLOCKWISE, so this
   is its inverse: display UV -> coded UV. */
'vec2 unrotate(vec2 d){',
'    if (uRotation == 90)  return vec2(d.y, 1.0 - d.x);',
'    if (uRotation == 180) return vec2(1.0 - d.x, 1.0 - d.y);',
'    if (uRotation == 270) return vec2(1.0 - d.y, d.x);',
'    return d;',
'}',
'void main(){',
/*  vUv.y grows upward (GL framebuffer); everything below works in DISPLAY
    space with the origin at the top-left, the convention the source texture,
    FFmpeg and the user all use. */
'    vec2 uv = vec2(vUv.x, 1.0 - vUv.y);',
'    if (uMode == 3) {',
'        float x0 = 0.5 - uSphereHalfW, x1 = 0.5 + uSphereHalfW;',
'        float y0 = 0.5 - uSphereHalfH, y1 = 0.5 + uSphereHalfH;',
'        if (uv.x < x0 || uv.x > x1 || uv.y < y0 || uv.y > y1) {',
'            gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return;',
'        }',
'        gl_FragColor = texture2D(uTex, unrotate(vec2((uv.x - x0) / (x1 - x0), (uv.y - y0) / (y1 - y0))));',
'        return;',
'    }',
'    float eye = uv.x < 0.5 ? 0.0 : 1.0;',
'    vec2 eyeUv = vec2((uv.x - 0.5 * eye) * 2.0, uv.y);',
'    vec2 src;',
'    if (uMode == 2) {',
/*       passthrough — the source already holds both eyes side by side, so the
         lens term applies to the WHOLE source frame, exactly as the FFmpeg
         chain applies lenscorrection before any splitting. */
'        vec2 whole = vec2(eyeUv.x * 0.5 + 0.5 * eye, eyeUv.y);',
'        src = vrLensDistort(whole, uBarrelK, uSrcAspect);',
'    } else if (uMode == 1) {',
/*       parallax — each eye crops (1-p) of the width, right eye offset by p */
'        float p = uParallaxFrac;',
'        vec2 whole = vec2(mix(p * eye, (1.0 - p) + p * eye, eyeUv.x), eyeUv.y);',
'        src = vrLensDistort(whole, uBarrelK, uSrcAspect);',
'    } else {',
'        src = vrLensDistort(eyeUv, uBarrelK, uSrcAspect);',
'    }',
'    if (src.x < 0.0 || src.x > 1.0 || src.y < 0.0 || src.y > 1.0) {',
'        gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return;',
'    }',
'    gl_FragColor = texture2D(uTex, unrotate(src));',
'}'
].join('\n');

function compile(gl, type, src){
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src); gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        var log = gl.getShaderInfoLog(sh); gl.deleteShader(sh);
        throw new Error('vr-render shader: ' + log);
    }
    return sh;
}

/**
 * Offscreen renderer for the hardware lane.
 *   var r = VRRender.createRenderer({ width: g.outW, height: g.outH });
 *   r.render(videoFrameOrCanvas, srcW, srcH, settings);   // draws into r.canvas
 * `source` may be a VideoFrame, <video>, canvas or ImageBitmap — anything
 * WebGL accepts as a texture source.
 */
function createRenderer(opts){
    opts = opts || {};
    var canvas = document.createElement('canvas');
    canvas.width  = Math.max(2, opts.width  || 2);
    canvas.height = Math.max(2, opts.height || 2);
    var glOpts = { alpha:false, antialias:false, preserveDrawingBuffer:true, premultipliedAlpha:false };
    var gl = canvas.getContext('webgl', glOpts) || canvas.getContext('experimental-webgl', glOpts);
    if (!gl) throw new Error('vr-render: no webgl');

    /* Refuse loudly rather than draw black. Side-by-side output is WIDE — a 4K
       source capped to Level 5.1 is still 5430 px across — and plenty of mobile
       GPUs stop at MAX_TEXTURE_SIZE / MAX_RENDERBUFFER_SIZE 4096. Over the
       limit WebGL does not error out of drawArrays; it just produces nothing,
       which reaches the user as a black video that took three minutes to make.
       The caller treats a throw here as "not eligible" and hands the file to
       the FFmpeg lane, which has no such limit. `srcMax` is checked too: the
       source frame becomes a texture. */
    var texMax = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0;
    var rbMax  = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) || texMax;
    var need   = Math.max(canvas.width, canvas.height, +opts.srcMax || 0);
    if (texMax && need > Math.min(texMax, rbMax)) {
        try { var lose = gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext(); } catch(_){}
        throw new Error('vr-render: needs ' + need + 'px, GPU allows ' + Math.min(texMax, rbMax));
    }

    var prog = gl.createProgram();
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('vr-render link: ' + gl.getProgramInfoLog(prog));
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
    var aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    /* No flip on upload: the shader works in display space (top-left origin),
       which is exactly how a video source uploads with flip OFF. */
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.activeTexture(gl.TEXTURE0);

    var U = {};
    ['uTex','uMode','uBarrelK','uSrcAspect','uParallaxFrac','uSphereHalfW','uSphereHalfH','uRotation']
        .forEach(function(n){ U[n] = gl.getUniformLocation(prog, n); });
    gl.uniform1i(U.uTex, 0);

    var MODE_INT = { sbs:0, parallax:1, passthrough:2, sphere:3 };

    return {
        canvas: canvas,
        gl: gl,
        resize: function(w, h){
            if (canvas.width === w && canvas.height === h) return;
            canvas.width = Math.max(2, w); canvas.height = Math.max(2, h);
        },
        /* srcW/srcH are DISPLAY dimensions (what the user saw), `rotation` is
           the tkhd angle the player applies clockwise — 0 for anything already
           upright, e.g. a <video> element or a canvas. */
        render: function(source, srcW, srcH, s, rotation){
            /* An HLG/PQ frame uploads as its raw coded signal — the HDR → SDR
               door (hdr-to-sdr.js, docs/hdr-to-sdr.md) hands the SDR picture,
               same size and orientation, or the source itself when it is SDR. */
            if (window.HdrToSdr && ((typeof VideoFrame !== 'undefined' && source instanceof VideoFrame) ||
                (typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement))) {
                source = window.HdrToSdr.image(source, { texture: true });
            }
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, source);
            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.uniform1i(U.uRotation, ((rotation | 0) % 360 + 360) % 360);
            gl.uniform1i(U.uMode, MODE_INT[s.mode] != null ? MODE_INT[s.mode] : 0);
            var bk = barrelK(s);
            gl.uniform2f(U.uBarrelK, bk ? bk.k1 : 0, bk ? bk.k2 : 0);
            gl.uniform1f(U.uSrcAspect, srcW / Math.max(1, srcH));
            gl.uniform1f(U.uParallaxFrac, parallaxFrac(s));
            if (s.mode === 'sphere') {
                /* Same fit-and-centre maths as the FFmpeg scale+pad above. */
                var covFrac = (s.coverage || 180) / 360;
                var boxWpx = canvas.width * covFrac, boxHpx = canvas.height;
                var k = Math.min(boxWpx / srcW, boxHpx / srcH);
                gl.uniform1f(U.uSphereHalfW, (srcW * k) / canvas.width  / 2);
                gl.uniform1f(U.uSphereHalfH, (srcH * k) / canvas.height / 2);
            }
            gl.drawArrays(gl.TRIANGLES, 0, 6);
        },
        destroy: function(){
            try { gl.deleteTexture(tex); gl.deleteBuffer(buf); gl.deleteProgram(prog); } catch(_){}
            try { var ext = gl.getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); } catch(_){}
            try { canvas.width = canvas.height = 1; } catch(_){}
        }
    };
}

window.VRRender = {
    MAX_CODED_AREA: MAX_CODED_AREA,
    HEADSET_LENS: HEADSET_LENS,
    SPHERE_CANVAS: SPHERE_CANVAS,
    PER_EYE: PER_EYE,
    HEADSET_BARREL: HEADSET_BARREL,
    BARREL_MULT: BARREL_MULT,
    GLSL_DISTORT: GLSL_DISTORT,
    geometry: geometry,
    ffmpegChain: ffmpegChain,
    barrelK: barrelK,
    parallaxFrac: parallaxFrac,
    createRenderer: createRenderer,
    version: '1.0.0'
};

})();
